import checkIcon from 'assets/icons/checkIcon.svg';
import commentIcon from 'assets/icons/commentIcon.svg';
import saveIcon from 'assets/icons/saveIcon.svg';
import BulkEditNoData from 'components/BulkEditNoData/BulkEditNoData';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { GridSectionProps } from 'Interfaces/FDPInterfaces/unconstrainedForecastBulkEdit';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useRef, useState } from 'react';
import NotesSection from './components/NotesSection/NotesSection';
import GridSkeletalLoader from './components/GridSectionSkeleton';
import classes from './GridSection.module.scss';
import { useGridSection } from './useGridSection.logic';

// Fixed left offsets for sticky non-month columns (px-based to avoid sub-pixel gaps)
const COL_EXPAND_W = 44;
const COL_CONCEPT_W = 120;
const COL_NDC_W = 120;
const COL_SUPPLIER_W = 120;
const COL_CONTRACT_W = 110;
const COL_MODE_W = 90;
const COL_STATUS_W = 80;

const LEFT_EXPAND = 0;
const LEFT_CONCEPT = LEFT_EXPAND + COL_EXPAND_W;
const LEFT_NDC = LEFT_CONCEPT + COL_CONCEPT_W;
const LEFT_SUPPLIER = LEFT_NDC + COL_NDC_W;
const LEFT_CONTRACT = LEFT_SUPPLIER + COL_SUPPLIER_W;
const LEFT_MODE = LEFT_CONTRACT + COL_CONTRACT_W;
const LEFT_STATUS = LEFT_MODE + COL_MODE_W;

const actionBg = '#ffffff';

const GridSection: React.FC<GridSectionProps> = ({
  loading,
  data,
  setData,
  setCopyData,
  setIsDataLoading,
  getForecastValue,
  setForecastValue,
  isRowEdited: _isRowEdited,
  isBtnEnabled,
  isNotesBtnEnabled,
  isSalveAllBtnEnabled,
  ndcError,
  handleSingleSave,
  handleSaveAllEdited,
  ndcSaveLoading,
  isSaveAllLoading,
  isNotesLoading,
  notesData,
  isNoteSaving,
  getNotesData,
  handleAddNote,
  loadMore,
  hasMore: _hasMore,
  nextPageLoading,
  singleSaveModalVisibility,
  setSingleSaveModalVisibility,
  saveAllModalVisibility,
  setSaveAllModalVisibility,
  setNotesData,
  isNoteEdited,
  setIsNoteEdited,
  handleCopy,
  handleEditNote,
  handleDeleteNote,
  deletingNote,
  isNoteDeleting,
  isDeleteClicked,
  setIsDeleteClicked,
  setDeletingNote,
  initialLoading,
  toggleRowExpansion,
  isRowExpanded,
  getBaselineValue,
  handleConceptSort,
  getModeState,
  handleModeToggle,
  getRowStatus,
  filterpayload,
}) => {
  const {
    forecastingMonthsEdit,
    priorMonthLabel: _priorMonthLabel,
    notesModalVisibility,
    clickedRowNDC,
    savingNDC,
    setSavingNDC,
    savingIndex,
    setSavingIndex,
    setClickedRowNDC,
    setNotesModalVisibility,
  } = useGridSection({ loading, setData, setCopyData, setIsDataLoading });

  const tableRef = useRef<HTMLDivElement>(null);
  const [isCopyDotVisible, setIsCopyDotVisible] = useState<Set<string>>(new Set());
  const conceptSortOverlay = useRef<OverlayPanel>(null);

  // Keep a stable ref to the latest loadMore so the scroll listener never captures stale closures
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  });

  // Re-run whenever loading changes: when the skeleton hides (loading: true→false),
  // div.container is a brand-new DOM node and needs a fresh scroll listener.
  useEffect(() => {
    if (loading) return;
    const container = tableRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMoreRef.current();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loading]);

  const getSortIcon = (sortType: string): string => {
    switch (sortType) {
      case 'asc':
        return 'pi pi-sort-up';
      case 'desc':
        return 'pi pi-sort-down';
      default:
        return 'pi pi-sort';
    }
  };

  const getSortLabel = (sortType: string): string => {
    switch (sortType) {
      case 'asc':
        return 'Ascending';
      case 'desc':
        return 'Descending';
      default:
        return 'No Sorting';
    }
  };

  const getSortMenuItems = () => {
    const currentSort = filterpayload.ConceptSort;
    return [
      {
        label: 'Ascending',
        icon: 'pi pi-sort-up',
        disabled: currentSort === 'asc',
        command: () => {
          handleConceptSort('asc');
          conceptSortOverlay.current?.hide();
        },
      },
      {
        label: 'Descending',
        icon: 'pi pi-sort-down',
        disabled: currentSort === 'desc',
        command: () => {
          handleConceptSort('desc');
          conceptSortOverlay.current?.hide();
        },
      },
      {
        label: 'Remove Sorting',
        icon: 'pi pi-times',
        disabled: currentSort === '',
        command: () => {
          handleConceptSort('');
          conceptSortOverlay.current?.hide();
        },
      },
    ];
  };

  const rowBg = (idx: number) => (idx % 2 ? '#F7F3FE' : '#FFFFFF');

  // Expanded row spans: expand col + concept + ndc + supplier + contract + mode + status = 7 cols
  const EXPANDED_LABEL_COLSPAN = 7;

  return (
    <>
      {loading ? (
        <GridSkeletalLoader />
      ) : (
        <>
          <div className={classes.container} ref={tableRef}>
            <table className={classes.tableStyling}>
              <thead className={classes.tableHeading}>
                <tr>
                  {/* Expand col */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_EXPAND_W, minWidth: COL_EXPAND_W, maxWidth: COL_EXPAND_W, left: LEFT_EXPAND, borderTopLeftRadius: '8px' }}
                  />

                  {/* Concept */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_CONCEPT_W, minWidth: COL_CONCEPT_W, maxWidth: COL_CONCEPT_W, left: LEFT_CONCEPT, whiteSpace: 'normal', wordBreak: 'break-word' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>Concept</span>
                      <Button
                        icon={getSortIcon(filterpayload.ConceptSort)}
                        className="p-button-text p-button-sm"
                        onClick={e => conceptSortOverlay.current?.toggle(e)}
                        style={{ fontSize: '12px', padding: '0.25rem', minWidth: 'auto', height: 'auto', color: 'white' }}
                        tooltip={`Current: ${getSortLabel(filterpayload.ConceptSort)}`}
                        tooltipOptions={{ position: 'top' }}
                      />
                      <OverlayPanel ref={conceptSortOverlay} className="p-overlaypanel-sm">
                        <Menu model={getSortMenuItems()} />
                      </OverlayPanel>
                    </div>
                  </th>

                  {/* NDC11 */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_NDC_W, minWidth: COL_NDC_W, maxWidth: COL_NDC_W, left: LEFT_NDC }}
                  >
                    NDC11
                  </th>

                  {/* Supplier Name */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_SUPPLIER_W, minWidth: COL_SUPPLIER_W, maxWidth: COL_SUPPLIER_W, left: LEFT_SUPPLIER }}
                  >
                    Supplier Name
                  </th>

                  {/* Contract Type */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_CONTRACT_W, minWidth: COL_CONTRACT_W, maxWidth: COL_CONTRACT_W, left: LEFT_CONTRACT }}
                  >
                    Contract Type
                  </th>

                  {/* Mode */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_MODE_W, minWidth: COL_MODE_W, maxWidth: COL_MODE_W, left: LEFT_MODE }}
                  >
                    Mode
                  </th>

                  {/* Status */}
                  <th
                    className={`${classes.thContent} ${classes.stickyHeader}`}
                    style={{ width: COL_STATUS_W, minWidth: COL_STATUS_W, maxWidth: COL_STATUS_W, left: LEFT_STATUS }}
                  >
                    Status
                  </th>

                  {/* Month columns */}
                  {forecastingMonthsEdit.map((item, idx) => (
                    <th
                      key={idx}
                      className={classes.thContent}
                    >
                      {item.label}
                    </th>
                  ))}

                  {/* Save All */}
                  <th
                    className={classes.saveAllButtonHeading}
                    style={{
                      borderTopRightRadius: '8px',
                      position: 'sticky',
                      right: 0,
                      backgroundColor: actionBg,
                      zIndex: 11,
                    }}
                  >
                    <Button
                      className={classes.saveAllBtn}
                      disabled={!isSalveAllBtnEnabled()}
                      onClick={() => setSaveAllModalVisibility(prev => !prev)}
                    >
                      <img src={checkIcon} alt="check" style={{ width: '36px', height: '30px', paddingRight: '1rem' }} />
                      Save All
                    </Button>
                  </th>
                </tr>
              </thead>

              <tbody className={classes.tableBody}>
                {data &&
                  data.map((item, idx) => {
                    const isExpanded = isRowExpanded(idx);
                    const isManual = getModeState(idx);
                    const bg = rowBg(idx);
                    const errorBg = ndcError.has(item.NDC11) ? '#fbbaba' : bg;

                    return (
                      <React.Fragment key={`row-${item.NDC11}-${idx}`}>
                        {/* Main Row */}
                        <tr className={classes.trContent} style={{ backgroundColor: bg }}>
                          {/* Expand button — always visible */}
                          <td
                            className={classes.stickyCell}
                            style={{ textAlign: 'center', width: COL_EXPAND_W, minWidth: COL_EXPAND_W, maxWidth: COL_EXPAND_W, left: LEFT_EXPAND, backgroundColor: bg }}
                          >
                            <Button
                              icon={isExpanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                              className="p-button-text p-button-plain"
                              onClick={() => toggleRowExpansion(idx)}
                              style={{ minWidth: 'auto', padding: '0.25rem' }}
                              title="View Prior Month Forecast"
                            />
                          </td>

                          {/* Concept */}
                          <td
                            className={classes.stickyCell}
                            style={{ width: COL_CONCEPT_W, minWidth: COL_CONCEPT_W, maxWidth: COL_CONCEPT_W, left: LEFT_CONCEPT, fontSize: '11px', backgroundColor: errorBg, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}
                          >
                            {item.Concept}
                          </td>

                          {/* NDC11 */}
                          <td
                            className={classes.stickyCell}
                            style={{ width: COL_NDC_W, minWidth: COL_NDC_W, maxWidth: COL_NDC_W, left: LEFT_NDC, fontSize: '12px', backgroundColor: errorBg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {item.NDC11}
                          </td>

                          {/* Supplier Name */}
                          <td
                            className={classes.stickyCell}
                            style={{ width: COL_SUPPLIER_W, minWidth: COL_SUPPLIER_W, maxWidth: COL_SUPPLIER_W, left: LEFT_SUPPLIER, fontSize: '11px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}
                          >
                            {item.SupplierName}
                          </td>

                          {/* Contract Type */}
                          <td
                            className={classes.stickyCell}
                            style={{ width: COL_CONTRACT_W, minWidth: COL_CONTRACT_W, maxWidth: COL_CONTRACT_W, left: LEFT_CONTRACT, textAlign: 'center' }}
                          >
                            {item.ContractType && (
                              <span
                                className={`${classes.contractChip} ${item.ContractType === 'MAS' ? classes.chipMas : classes.chipNc}`}
                              >
                                {item.ContractType}
                              </span>
                            )}
                          </td>

                          {/* Mode toggle */}
                          <td
                            className={classes.stickyCell}
                            style={{ textAlign: 'center', width: COL_MODE_W, minWidth: COL_MODE_W, maxWidth: COL_MODE_W, left: LEFT_MODE }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <InputSwitch
                                checked={isManual}
                                onChange={e => handleModeToggle(idx, e.value)}
                              />
                            </div>
                          </td>

                          {/* Status */}
                          <td
                            className={classes.stickyCell}
                            style={{ textAlign: 'center', width: COL_STATUS_W, minWidth: COL_STATUS_W, maxWidth: COL_STATUS_W, left: LEFT_STATUS, backgroundColor: bg }}
                          >
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: '500',
                                color: getRowStatus(idx) === 'Model' ? '#059669' : '#dc2626',
                              }}
                            >
                              {getRowStatus(idx)}
                            </span>
                          </td>

                          {/* Monthly Input Fields */}
                          {forecastingMonthsEdit.map((month, mnt_idx) => {
                            const fdEntry = item.forecast_data.find(
                              fd => fd.ForecastPeriod === month.dateString,
                            );
                            const isDeramped = fdEntry?.is_deramped === true;
                            const derampTitle = isDeramped
                              ? `NC De-ramp applied — contract term date: ${item.ContractEndDate ?? ''}`
                              : undefined;
                            return (
                              <td
                                key={mnt_idx}
                                style={{ position: 'relative' }}
                                title={derampTitle}
                              >
                                <InputText
                                  className={classes.input}
                                  value={getForecastValue(item.NDC11, month.dateString, mnt_idx)}
                                  onFocus={() =>
                                    setIsCopyDotVisible(() => {
                                      const temp = new Set('');
                                      temp.add(item.NDC11.toString() + idx.toString() + month.dateString);
                                      return temp;
                                    })
                                  }
                                  onChange={e =>
                                    setForecastValue(idx, month.dateString, e.target.value.replace(/,/g, ''), mnt_idx)
                                  }
                                />
                                {isCopyDotVisible.has(item.NDC11.toString() + idx.toString() + month.dateString) &&
                                  mnt_idx < 14 && (
                                    <span
                                      title="Copy value in all cells to the right of current row"
                                      className={classes.copyPointerBtn}
                                      onClick={() => {
                                        handleCopy(idx, month.dateString);
                                        setIsCopyDotVisible(new Set());
                                      }}
                                      style={{ position: 'absolute', bottom: '6px', right: '8px' }}
                                    >
                                      .
                                    </span>
                                  )}
                              </td>
                            );
                          })}

                          {/* Action Buttons */}
                          <td
                            style={{
                              position: 'sticky',
                              right: 0,
                              backgroundColor: bg,
                              zIndex: 1,
                            }}
                          >
                            <div className={classes.btnContainer}>
                              <Button
                                className={classes.saveBtn}
                                disabled={!isBtnEnabled(idx)}
                                onClick={() => {
                                  setSingleSaveModalVisibility(prev => !prev);
                                  setSavingIndex(idx);
                                  setSavingNDC(item.NDC11);
                                }}
                              >
                                <img src={saveIcon} alt="save" style={{ paddingRight: '1rem' }} />
                                Save
                              </Button>
                              <Button
                                className={classes.commentBtn}
                                disabled={!isNotesBtnEnabled(idx)}
                                onClick={() => {
                                  setNotesModalVisibility(prev => !prev);
                                  setClickedRowNDC(item.NDC11);
                                  getNotesData(item.NDC11);
                                }}
                              >
                                <img src={commentIcon} alt="comment" />
                              </Button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Row — previous planning period (prior cycle) values */}
                        {isExpanded && (
                          <tr
                            key={`expanded-${idx}`}
                            className={classes.expandedRow}
                            style={{ backgroundColor: '#eef2ff', borderTop: '1px solid #c7d2fe' }}
                          >
                            <td
                              style={{
                                position: 'sticky',
                                left: LEFT_EXPAND,
                                width: COL_EXPAND_W,
                                backgroundColor: '#eef2ff',
                                zIndex: 2,
                              }}
                            />
                            <td
                              colSpan={EXPANDED_LABEL_COLSPAN - 1}
                              style={{
                                padding: '0.4rem 0.5rem',
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#7c5aa4',
                                position: 'sticky',
                                left: LEFT_CONCEPT,
                                backgroundColor: '#eef2ff',
                                zIndex: 2,
                                boxShadow: '3px 0 6px -2px rgba(0,0,0,0.15)',
                              }}
                            >
                              Prior Month Forecast
                            </td>
                            {forecastingMonthsEdit.map((month, priorMnt_idx) => (
                              <td
                                key={month.dateString}
                                style={{
                                  padding: '0.4rem 0.3rem',
                                  fontSize: '11px',
                                  textAlign: 'center',
                                  color: '#4b5563',
                                }}
                              >
                                {getBaselineValue(idx, month.dateString, priorMnt_idx)}
                              </td>
                            ))}
                            <td
                              style={{
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#eef2ff',
                                zIndex: 2,
                                minWidth: '15rem',
                              }}
                            />
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                {(nextPageLoading || loading) && (
                  <tr>
                    {forecastingMonthsEdit.map((_, idx) => {
                      if (idx === 8) {
                        return (
                          <td key={idx}>
                            <ProgressSpinner strokeWidth="" style={{ width: '3.5rem', height: '5rem' }} />
                          </td>
                        );
                      }
                      return <td key={idx} />;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
            {!initialLoading && data.length === 0 && !nextPageLoading && (
              <BulkEditNoData label="No Unconstrained Forecast data available for the applied filter values!!" />
            )}
          </div>

          <NotesSection
            ndc={clickedRowNDC}
            visibility={notesModalVisibility}
            handleCancel={() => setNotesModalVisibility(prev => !prev)}
            isNotesLoading={isNotesLoading}
            notesData={notesData}
            setNotesData={setNotesData}
            isNoteSaving={isNoteSaving}
            handleAddNote={handleAddNote}
            isNoteEdited={isNoteEdited}
            setIsNoteEdited={setIsNoteEdited}
            handleEditNote={handleEditNote}
            handleDeleteNote={handleDeleteNote}
            deletingNote={deletingNote}
            isNoteDeleting={isNoteDeleting}
            isDeleteClicked={isDeleteClicked}
            setIsDeleteClicked={setIsDeleteClicked}
            setDeletingNote={setDeletingNote}
          />

          <ConfirmationModal
            visibility={singleSaveModalVisibility}
            modalHeading="Confirmation"
            modalContent="Are you sure want to update the Unconstrained Forecast?"
            loading={ndcSaveLoading.get(savingNDC)}
            confirmLabel="Update"
            handleCancel={() => setSingleSaveModalVisibility(prev => !prev)}
            handleConfirm={() => handleSingleSave(savingIndex)}
          />

          <ConfirmationModal
            visibility={saveAllModalVisibility}
            modalHeading="Confirmation"
            modalContent="Are you sure want to create the Unconstrained Forecast for all NDCs?"
            loading={isSaveAllLoading}
            confirmLabel="Update All"
            handleCancel={() => setSaveAllModalVisibility(prev => !prev)}
            handleConfirm={() => {
              handleSaveAllEdited();
              setNotesModalVisibility(false);
            }}
          />
        </>
      )}
    </>
  );
};

export default GridSection;
